/** @jsxImportSource @emotion/react */

import dayjs from "dayjs";
import { format2Frontend } from "../../../../../../actions/Layers/util";
import { getFilledArrayOrDefault } from "../../../../../../utils";
import { getFruitTableDataLegacy } from "../../../../AddItem/components/Summary/LegacyFruitTable";

// The `/checks` endpoint returns each check's `meta` as an array of `{key, value}` pairs.
// Consistent with `format2Frontend` (which spreads layer meta onto the layer object), we
// spread the check meta directly onto the merged check so consumers read `check.status`
// rather than `check.meta.status` — keeping the same flat structure used everywhere else.
const flattenMeta = (meta) => {
    if (!Array.isArray(meta)) return meta ?? {};
    return meta.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {});
};

const mergeChecks = (layerChecks) => {
    const merged = layerChecks.reduce(
        (acc, { avocados, defects, defect_list, images, all_images, ...i }) => ({
            ...acc,
            ...i,
            [`inspected_by_${i.location}`]: i.inspected_by,
            avocados: [...acc.avocados, ...getFilledArrayOrDefault(avocados)],
            images: [...acc.images, ...getFilledArrayOrDefault(images)],
            all_images: [...acc.all_images, ...getFilledArrayOrDefault(all_images)],
            defects: { ...acc.defects, ...defects },
            defect_list: [...acc.defect_list, ...getFilledArrayOrDefault(defect_list)],
        }),
        {
            avocados: [],
            images: [],
            all_images: [],
            defects: {},
            defect_list: []
        }
    );
    return { ...merged, ...flattenMeta(merged.meta) };
};

export const setPDFDataForIntakeReport = (config, user, tenantDefectDefinitions, children, checks, options: any = {}) => {
    const {
        preferred_check_locations = [],
        fallback_check_locations = [],
        fallback_check_max_age_days,
        returnOnlyDirectChildrenChecks = true
    } = options;

    const preferred_locations_set = new Set(getFilledArrayOrDefault(preferred_check_locations));
    const fallback_locations_set = new Set(getFilledArrayOrDefault(fallback_check_locations));

    const children_with_intake = children.results
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((l) => {
            const layer_checks = checks.filter((i) => !returnOnlyDirectChildrenChecks || i.layer_id === l.id);

            const selected_checks = layer_checks.filter((check) => preferred_locations_set.has(check.location));
            const fallback_checks = selected_checks.length === 0
                ? layer_checks.filter((check) => {
                    if (!fallback_locations_set.has(check.location)) return false;
                    if (fallback_check_max_age_days != null && check.created) {
                        const checkAge = dayjs().diff(dayjs(check.created), "day");
                        return checkAge <= fallback_check_max_age_days;
                    }
                    return true;
                })
                : [];
            let checks_to_merge = [];
            const usedFallback = selected_checks.length === 0 && fallback_checks.length > 0;

            if (selected_checks.length > 0) {
                checks_to_merge = selected_checks;
            } else if (fallback_checks.length > 0) {
                checks_to_merge = fallback_checks;
            } else if (preferred_locations_set.size === 0 && fallback_locations_set.size === 0) {
                checks_to_merge = layer_checks;
            }
            const merged_check = mergeChecks(checks_to_merge);

            return {
                ...l,
                latest_check: merged_check,
                _usedFallback: usedFallback,
                _fallbackLocation: usedFallback ? fallback_checks[0]?.location : undefined,
            };
        })
        .filter((l) => l.latest_check?.check_id)
        .map(format2Frontend)
        .map((child) => {
            // When fallback checks are used, copy the fallback location's status to each preferred location
            // so that calculateStatusPercentages (which looks for `{checkType}_status`) finds the inherited status
            if (!child._usedFallback || !child._fallbackLocation) return child;
            const fallbackLoc = child._fallbackLocation;
            const statusSuffix = "_status";
            // The `{location}_status` meta is not always present on nested children returned by
            // `/layers/{id}/children` (e.g. pallets fetched via `children_level=2`). Fall back to
            // the merged check's status — `mergeChecks` spreads meta keys directly onto the check
            // object (same pattern as `format2Frontend`), so `latest_check.status` holds the value.
            const fallbackStatus = child[`${fallbackLoc}${statusSuffix}`]
                ?? child.latest_check?.status;
            if (!fallbackStatus) return child;
            return preferred_check_locations.reduce((acc, loc) => {
                acc[`${loc}${statusSuffix}`] = fallbackStatus;
                return acc;
            }, { ...child });
        });

    // * Populate the intake check with fruit table data
    const updatedChildren = children_with_intake
        .map((child) => (
            // we overwrite the latest check here because the `/checks/{check_id}` endpoint will also return all images and underlying avocados while the layer endpoint does not
            {
                ...child,
                latest_check: {
                    ...child.latest_check,
                    label: `${child.label}`, // label for the images
                    table_data: getFruitTableDataLegacy({ // this function generates the data for indivual fruit
                        layer: child,
                        check: child.latest_check,
                        fruits: child.latest_check.avocados,
                        config,
                        user,
                        tenantDefectDefinitions
                    }),
                    ...child.meta
                }
            }));

    const updatedChecks = updatedChildren
        .map((child) => ({ ...child.latest_check, layer: child }))
        .filter((check) => check?.check_id);

    return { children: updatedChildren, checks: updatedChecks };
};

export const setPDFDataForStorageReport = (config, user, tenantDefectDefinitions, children, checks) => {
    const formmated_checks_with_group_ids = checks.map((i) => ({
        ...i,
        group_id: `${i.layer_id}-${i.date}`
    }));

    const grouped = Object.values(formmated_checks_with_group_ids.reduce((acc, { avocados, defects, defect_list, images, ...i }) => {
        if (!acc[i.group_id]) {
            // * merge all checks done on same layer on same day
            const layer = children.results.find((l) => l.id === i.layer_id);

            // * If storage template is used, while enabled on a pallet. It will not find a child with this layer_id because the check is done on the layer itself.
            if (!layer) {
                return acc;
            }

            acc[i.group_id] = {
                ...layer,
                group_id: i.group_id,
                layer_id: i.layer_id,
                date: i.date,
                timestamp: i.timestamp,
                latest_check: {
                    ...i,
                    [`inspected_by_${i.location}`]: i.inspected_by,
                    avocados: getFilledArrayOrDefault(avocados),
                    images: getFilledArrayOrDefault(images),
                    defects: { ...defects },
                    defect_list: getFilledArrayOrDefault(defect_list),
                }
            };
            // * add to the already existing layer day group
        } else {
            acc[i.group_id] = {
                ...acc[i.group_id],
                latest_check: {
                    ...acc[i.group_id].latest_check,
                    [`inspected_by_${i.location}`]: i.inspected_by,
                    avocados: [...acc[i.group_id].latest_check.avocados, ...getFilledArrayOrDefault(avocados)],
                    images: [...acc[i.group_id].latest_check.images, ...getFilledArrayOrDefault(images)],
                    defects: { ...acc[i.group_id].latest_check.defects, ...defects },
                    defect_list: [...acc[i.group_id].latest_check.defect_list, ...getFilledArrayOrDefault(defect_list)]
                }
            };
        }
        return acc;
    }, {})).map(format2Frontend).sort((a, b) => a.label.localeCompare(b.label) || a.timestamp - b.timestamp);

    // * Populate the check with fruit table data
    const updatedChildren = grouped
        .map((child) => (
            // we overwrite the latest check here because the `/checks/{check_id}` endpoint will also return all images and underlying avocados while the layer endpoint does not
            {
                ...child,
                latest_check: {
                    ...child.latest_check,
                    label: `${child.label}`, // label for the images
                    table_data: getFruitTableDataLegacy({ // this function generates the data for indivual fruit TODO: refactor to fields api
                        layer: child,
                        check: child.latest_check,
                        fruits: child.latest_check.avocados,
                        config,
                        user,
                        tenantDefectDefinitions
                    }),
                    ...child.meta
                }
            }));

    const updatedChecks = updatedChildren.map((child) => child.latest_check);

    return { children: updatedChildren, checks: updatedChecks };
};
